import { useCallback, useEffect, useRef, useState } from 'react'
import { getTags } from '../api/gg'
import { GalleryPreview } from '../components/GalleryPreview'
import { InfiniteScrollSentinel } from '../components/InfiniteScrollSentinel'
import { Lightbox } from '../components/Lightbox'
import { MediaTile } from '../components/MediaTile'
import { TagPicker } from '../components/TagPicker'
import { useMediaFeed } from '../hooks/useMediaFeed'
import { buildFeedTags, formatSexualitySummary } from '../lib/feedPrefs'
import { useGame } from '../store/game'
import type { ContentKind, Tag } from '../types'

type Phase = 'pick' | 'loading' | 'view'

/** Gallery Mode — horizontal left/right scroll through the feed. */
export function GalleryMode() {
  const setSelection = useGame((s) => s.setSelection)
  const feedPrefs = useGame((s) => s.feedPrefs)

  const [phase, setPhase] = useState<Phase>('pick')
  const [tags, setTags] = useState<Tag[]>([])
  const [tagLabels, setTagLabels] = useState<string[]>([])
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [revealed, setRevealed] = useState(false)

  const railRef = useRef<HTMLDivElement | null>(null)
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)
  const bindRail = useCallback((node: HTMLDivElement | null) => {
    railRef.current = node
    setScrollRoot(node)
  }, [])

  const { items, progress, loading, loadingMore, hasMore, error, startBrowse, loadMore, cancel, setError } =
    useMediaFeed()

  useEffect(() => {
    void getTags().then((t) => setTags(t.map((x) => ({ ...x, enabled: true, sort: 0 }))))
    return () => cancel()
  }, [cancel])

  const start = useCallback(
    async (tagIds: string[], kind: ContentKind) => {
      const chosen = tags.filter((t) => tagIds.includes(t.id))
      if (!chosen.length) return
      setSelection(tagIds, kind)
      setTagLabels(chosen.map((t) => t.label))
      setError(null)
      setPhase('loading')
      setRevealed(false)

      const feed = await startBrowse(buildFeedTags(chosen, feedPrefs), kind, 16)
      if (!feed) {
        setPhase('pick')
        return
      }
      setLightbox(null)
      setPhase('view')
      window.setTimeout(() => setRevealed(true), 50)
    },
    [tags, setSelection, feedPrefs, startBrowse, setError],
  )

  const cancelLoad = () => {
    cancel()
    setPhase('pick')
  }

  const changeTags = () => {
    cancel()
    setLightbox(null)
    setRevealed(false)
    setPhase('pick')
  }

  const scrollBy = useCallback((dir: -1 | 1) => {
    const rail = railRef.current
    if (!rail) return
    rail.scrollBy({ left: dir * rail.clientWidth, behavior: 'smooth' })
  }, [])

  const onLoadMore = useCallback(() => loadMore(2), [loadMore])

  useEffect(() => {
    if (phase !== 'view' || lightbox !== null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') scrollBy(1)
      else if (e.key === 'ArrowLeft') scrollBy(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, lightbox, scrollBy])

  if (phase === 'pick')
    return (
      <main className="px-4 py-8">
        {error ? (
          <div className="mx-auto mb-4 max-w-2xl rounded-lg border-2 border-neon-red bg-neon-red/10 px-4 py-2 text-center font-bold text-neon-red">
            {error}
          </div>
        ) : null}
        <TagPicker tags={tags} onStart={start} ctaLabel="OPEN GALLERY ‹›" />
      </main>
    )

  if (phase === 'loading' || loading)
    return (
      <GalleryPreview
        modeLabel="GALLERY MODE"
        tags={tagLabels}
        sexuality={formatSexualitySummary(feedPrefs)}
        fraction={progress.fraction}
        count={progress.count}
        note={progress.note}
        onCancel={cancelLoad}
      />
    )

  return (
    <main className="px-3 py-4">
      <div className="mx-auto mb-3 flex max-w-6xl items-center justify-between">
        <h2 className="goon-title comic-title-cyan text-3xl">{items.length} IN FEED</h2>
        <p className="text-xs uppercase tracking-widest text-muted">‹ › or arrow keys</p>
        <button onClick={changeTags} className="btn-ghost text-sm">Change tags</button>
      </div>

      <div className="neon-grid-stage relative mx-auto max-w-6xl">
        <div className={`neon-light-sweep pointer-events-none ${revealed ? 'neon-light-sweep-active' : ''}`} aria-hidden />
        <div
          ref={bindRail}
          className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-2xl border border-edge bg-surface shadow-cyber"
        >
          {items.map((item, i) => (
            <section
              key={item.id}
              className="neon-grid-item flex w-full shrink-0 snap-center flex-col items-center justify-center gap-3 p-4"
              style={{ '--i': i } as React.CSSProperties}
            >
              {item.kind === 'video' ? (
                <MediaTile item={item} eager={i < 2} onClick={() => setLightbox(i)} className="max-h-[70vh] w-auto" />
              ) : (
                <img
                  src={item.fullsize ?? item.src}
                  alt={item.alt ?? ''}
                  loading={i < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                  draggable={false}
                  className="max-h-[70vh] w-auto rounded-xl border border-edge object-contain shadow-neon-pink"
                />
              )}
              <span className="rounded-full border border-pow/50 bg-pow/10 px-3 py-0.5 text-xs font-bold uppercase text-pow">
                {item.tag}
              </span>
              <p className="text-center text-sm text-muted">@{item.authorHandle ?? 'bluesky'}</p>
            </section>
          ))}
          <InfiniteScrollSentinel
            scrollRoot={scrollRoot}
            rootMargin="200px"
            onVisible={onLoadMore}
            disabled={!hasMore || loadingMore}
            className="flex w-32 shrink-0 items-center justify-center"
          />
        </div>

        <button
          onClick={() => scrollBy(-1)}
          aria-label="Previous"
          className="absolute left-2 top-1/2 z-30 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-kapow bg-surface2/90 text-3xl text-kapow shadow-neon-cyan backdrop-blur transition hover:bg-kapow/10"
        >
          ‹
        </button>
        <button
          onClick={() => scrollBy(1)}
          aria-label="Next"
          className="absolute right-2 top-1/2 z-30 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-kapow bg-surface2/90 text-3xl text-kapow shadow-neon-cyan backdrop-blur transition hover:bg-kapow/10"
        >
          ›
        </button>
      </div>

      {lightbox !== null ? (
        <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
      ) : null}
    </main>
  )
}